import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SpreadsheetHubPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-6">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 mb-6">
          <nav className="text-sm text-stone-500">
            <a href="/" className="hover:text-emerald-700 transition-colors">{t('breadcrumb_home')}</a>
            <span className="mx-2">/</span>
            <span className="text-stone-700">{t('breadcrumb_spreadsheet')}</span>
          </nav>
        </div>

        <article className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
          {/* Header */}
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-800 leading-tight mb-4">
              CSSBuy Spreadsheet: Complete Guide 2026
            </h1>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
              The CSSBuy Spreadsheet is one of the most useful resources for anyone researching products through buying agents. This complete guide explains what it is, how to use it, and what to check before making any purchase decisions.
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs md:text-sm text-stone-400">
              <span>{t('article_published')}: 2026-08-27</span>
              <span>{t('article_updated')}: 2026-08-27</span>
            </div>
          </header>

          {/* Quick Answer Box */}
          <div className="mb-10 p-5 md:p-6 rounded-xl bg-emerald-50 border border-emerald-100">
            <h2 className="text-lg md:text-xl font-semibold text-emerald-800 mb-3">
              {t('article_quick_answer')}
            </h2>
            <p className="text-sm md:text-base text-emerald-700 leading-relaxed">
              The CSSBuy Spreadsheet is an organized collection of product categories, links, and guides that helps buyers research items before purchasing through CSSBuy. It covers shoes, hoodies, t-shirts, jackets, accessories, and more. Always verify current links and product information before buying, as details may change.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="mb-10 p-5 md:p-6 rounded-xl bg-stone-50 border border-stone-200">
            <h3 className="text-base md:text-lg font-semibold text-stone-800 mb-4">{t('article_toc')}</h3>
            <ul className="space-y-2">
              <li><a href="#introduction" className="text-sm text-emerald-700 hover:underline">1. Introduction</a></li>
              <li><a href="#what-is" className="text-sm text-emerald-700 hover:underline">2. What is CSSBuy Spreadsheet?</a></li>
              <li><a href="#categories" className="text-sm text-emerald-700 hover:underline">3. Product Categories</a></li>
              <li><a href="#how-to-use" className="text-sm text-emerald-700 hover:underline">4. How to Use the Spreadsheet</a></li>
              <li><a href="#product-links" className="text-sm text-emerald-700 hover:underline">5. Product Links and Verification</a></li>
              <li><a href="#qc" className="text-sm text-emerald-700 hover:underline">6. Understanding QC Photos</a></li>
              <li><a href="#shipping" className="text-sm text-emerald-700 hover:underline">7. Shipping Information</a></li>
              <li><a href="#safety" className="text-sm text-emerald-700 hover:underline">8. Safety Tips</a></li>
              <li><a href="#faq" className="text-sm text-emerald-700 hover:underline">9. FAQ</a></li>
              <li><a href="#related" className="text-sm text-emerald-700 hover:underline">10. Related Resources</a></li>
            </ul>
          </div>

          {/* Introduction */}
          <section id="introduction" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              {t('article_introduction')}
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              When researching products through buying agents, having organized resources can save hours of time and reduce confusion. The CSSBuy Spreadsheet has emerged as a popular reference point for buyers looking to explore different product categories, compare options, and find reliable links. This guide is designed to help you understand how to use this resource effectively while staying safe throughout your buying journey.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              This website is an independent resource and does not officially represent CSSBuy. All information provided here is for research purposes only. Prices, product availability, links, and shipping details can change at any time. Always verify current information directly before making any purchase or payment.
            </p>
          </section>

          {/* What is */}
          <section id="what-is" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              What is CSSBuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              The CSSBuy Spreadsheet is essentially an organized directory of product categories and resources related to buying through the CSSBuy platform. It serves as a starting point for product research rather than a direct purchasing tool. The spreadsheet organizes items into logical categories such as footwear, clothing, accessories, and other goods, making it easier to browse through different options.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              Unlike a traditional store catalog, the CSSBuy Spreadsheet focuses on providing links and references that buyers can use to conduct their own research. Each category typically contains information about the types of products available, general price ranges, and guidance on what to look for when evaluating items. This structure helps both beginners and experienced buyers navigate the often-complex world of product sourcing.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              The value of the CSSBuy Spreadsheet lies in its organization and the community knowledge it represents. Rather than searching randomly across multiple platforms, buyers can use the spreadsheet as a structured starting point for their research. However, it is crucial to remember that all links and product information should be verified independently before any purchase.
            </p>
          </section>

          {/* Categories */}
          <section id="categories" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Product Categories in the CSSBuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              The CSSBuy Spreadsheet covers a wide range of product categories to help different types of buyers find what they are looking for. Here are the main categories typically found in the spreadsheet:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {[
                'Shoes and Sneakers',
                'Hoodies and Sweaters',
                'T-Shirts',
                'Jackets and Outerwear',
                'Pants and Shorts',
                'Headwear and Caps',
                'Matching Sets',
                'Underwear',
                'Jerseys and Sportswear',
                'Accessories',
                'Bags and Backpacks',
                'Belts',
                'Perfumes and Fragrances',
                'Other Items',
              ].map((cat) => (
                <div key={cat} className="flex items-center gap-2 p-3 rounded-lg bg-stone-50 border border-stone-200">
                  <i className="ri-check-line text-emerald-600" />
                  <span className="text-sm text-stone-700">{cat}</span>
                </div>
              ))}
            </div>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              Each category may contain subcategories or specific product types. When exploring a category, pay attention to the details provided about sizing, materials, and quality indicators. The spreadsheet is regularly updated to reflect new trends and seasonal items, but the frequency of updates can vary.
            </p>
          </section>

          {/* How to Use */}
          <section id="how-to-use" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              How to Use the CSSBuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              Using the CSSBuy Spreadsheet effectively requires a systematic approach. Here is a step-by-step process that most buyers follow:
            </p>
            <div className="space-y-4 mb-6">
              {[
                { step: 'Step 1: Choose Your Category', desc: 'Start by selecting the product category that matches what you are looking for. This narrows down your search and shows you relevant options.' },
                { step: 'Step 2: Review Available Options', desc: 'Browse through the items listed in your chosen category. Look at product images, descriptions, and any available details about materials and sizing.' },
                { step: 'Step 3: Verify Product Links', desc: 'Before clicking any link, check if it is from a trusted source. Links in the spreadsheet should be verified for functionality and safety.' },
                { step: 'Step 4: Check for QC Information', desc: 'Quality Control (QC) photos are essential for verifying product quality. Look for items that have QC documentation available.' },
                { step: 'Step 5: Compare Prices and Options', desc: 'Do not settle on the first option you see. Compare similar items across different sellers to understand price ranges and quality differences.' },
                { step: 'Step 6: Confirm Current Conditions', desc: 'Before finalizing any purchase, verify that all product information, prices, and seller details are current. Information can change rapidly.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-4 p-4 rounded-lg bg-stone-50 border border-stone-200">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold shrink-0 mt-0.5">
                    {item.step.split(':')[0].replace('Step ', '')}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-stone-800 mb-1">{item.step.split(':')[1]}</h4>
                    <p className="text-sm text-stone-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              Remember that the spreadsheet is a research tool, not a guarantee of quality or availability. Always do your own due diligence before any purchase.
            </p>
          </section>

          {/* Product Links */}
          <section id="product-links" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Product Links and Verification
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              One of the most important aspects of using the CSSBuy Spreadsheet is understanding how to verify product links. Links in the spreadsheet are provided as starting points for research, but they require careful evaluation before use.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              When you encounter a link in the spreadsheet, check the following before proceeding: ensure the link is active and loads correctly, verify the source domain is legitimate, check if the product page matches the description in the spreadsheet, and look for any security warnings from your browser. If a link seems suspicious or does not load properly, it is better to skip it and look for alternative options.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              Links can become outdated or change without notice. If you find a broken link, it does not necessarily mean the product is unavailable, but it does mean you will need to do additional research to find the current link. This is why independent verification is always recommended.
            </p>
          </section>

          {/* QC */}
          <section id="qc" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Understanding QC Photos
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              QC (Quality Control) photos are one of the most valuable tools available to buyers. These photos are taken at the warehouse after your item arrives but before it is shipped to you. They show the actual product you will receive, allowing you to verify quality before finalizing the shipment.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              When reviewing QC photos, pay attention to stitching quality, material texture, color accuracy, logo placement and quality, and any visible defects or irregularities. If the QC photos show something different from what you expected, you typically have the option to request an exchange or return before the item is shipped.
            </p>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              Not all items in the spreadsheet will have QC photos available from the start. Some sellers provide example QC photos, while others only provide them after you place an order. Understanding this process helps set realistic expectations and ensures you know what to check when QC photos become available.
            </p>
          </section>

          {/* Shipping */}
          <section id="shipping" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Shipping Information
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              Shipping is one of the most important factors to consider when using the CSSBuy Spreadsheet for product research. The total cost of your purchase includes not just the product price, but also shipping fees, potential customs charges, and any insurance costs.
            </p>
            <div className="overflow-hidden rounded-xl border border-stone-200 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-100">
                    <th className="px-4 py-3 text-left font-semibold text-stone-700">Factor</th>
                    <th className="px-4 py-3 text-left font-semibold text-stone-700">Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { factor: 'Package Weight', impact: 'Heavier items cost more to ship' },
                    { factor: 'Volumetric Weight', impact: 'Large but light items may be charged by size' },
                    { factor: 'Destination Country', impact: 'Different rates for different regions' },
                    { factor: 'Shipping Method', impact: 'Express costs more but is faster' },
                    { factor: 'Customs Rules', impact: 'May require additional fees or documentation' },
                    { factor: 'Insurance', impact: 'Optional but recommended for expensive items' },
                  ].map((row, idx) => (
                    <tr key={row.factor} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}>
                      <td className="px-4 py-3 font-medium text-stone-700 border-r border-stone-100">{row.factor}</td>
                      <td className="px-4 py-3 text-stone-600">{row.impact}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              For more detailed shipping information, visit our <a href="/cssbuy-shipping-cost/" className="text-emerald-700 hover:underline">CSSBuy Shipping Cost Guide</a>.
            </p>
          </section>

          {/* Safety */}
          <section id="safety" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Safety Tips When Using the CSSBuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
              Safety should always be your top priority when researching products online. Here are essential safety tips to keep in mind:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Always verify that links are legitimate before clicking them',
                'Never share personal payment information through unofficial channels',
                'Check seller reputation and reviews when available',
                'Use QC photos to verify product quality before shipping',
                'Understand the return and refund policies before purchasing',
                'Keep records of all transactions and communications',
                'Be aware of customs regulations in your country',
                'Start with smaller purchases to test the process',
              ].map((tip) => (
                <li key={tip} className="flex items-start gap-2">
                  <i className="ri-shield-check-line text-emerald-600 mt-0.5 shrink-0" />
                  <span className="text-sm md:text-base text-stone-600">{tip}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm md:text-base text-stone-600 leading-relaxed">
              This website is an independent resource and does not officially represent CSSBuy. We do not guarantee the accuracy of any product information, links, or prices listed in the spreadsheet or on this website. Always verify current information independently before making any purchase or payment.
            </p>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                { q: 'Is the CSSBuy Spreadsheet free to use?', a: 'Yes, the CSSBuy Spreadsheet is a free resource for product research. There is no cost to browse the categories and links.' },
                { q: 'How often is the spreadsheet updated?', a: 'Update frequency varies. Some categories are updated regularly with new items, while others may be updated less frequently. Always verify current links.' },
                { q: 'Can I trust all links in the spreadsheet?', a: 'Links should be verified independently. While efforts are made to maintain quality links, they can change or become outdated. Always verify before purchasing.' },
                { q: 'What should I do if a product is different from the photos?', a: 'If QC photos show a different product than expected, contact your agent immediately to discuss options before the item is shipped.' },
                { q: 'Are prices in the spreadsheet guaranteed?', a: 'No, prices are for reference only and can change. Always check current prices before purchasing.' },
              ].map((faq, idx) => (
                <div key={idx} className="p-4 md:p-5 rounded-xl bg-stone-50 border border-stone-200">
                  <h4 className="text-sm font-semibold text-stone-800 mb-2">{faq.q}</h4>
                  <p className="text-sm text-stone-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related Resources */}
          <section id="related" className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              {t('article_related')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { title: 'CSSBuy Review: Complete Analysis', link: '/cssbuy-review/' },
                { title: 'CSSBuy Shipping Cost Guide', link: '/cssbuy-shipping-cost/' },
                { title: 'Is CSSBuy Legit? Safety Checklist', link: '/cssbuy-legit/' },
                { title: 'CSSBuy Warehouse Guide', link: '/cssbuy-warehouse/' },
                { title: 'CSSBuy Coupon and Discount Guide', link: '/cssbuy-coupon/' },
                { title: 'CSSBuy QC Photo Guide', link: '/cssbuy-qc/' },
              ].map((related) => (
                <a
                  key={related.title}
                  href={related.link}
                  className="flex items-center gap-2 p-3 rounded-lg bg-stone-50 border border-stone-200 hover:border-emerald-200 hover:bg-white transition-all"
                >
                  <i className="ri-arrow-right-line text-emerald-600 shrink-0" />
                  <span className="text-sm text-stone-700 hover:text-emerald-700 transition-colors">{related.title}</span>
                </a>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12 p-6 md:p-8 rounded-xl bg-stone-50 border border-stone-200 text-center">
            <h3 className="text-xl md:text-2xl font-bold text-stone-800 mb-3">
              Ready to explore CSSBuy Spreadsheet?
            </h3>
            <p className="text-sm md:text-base text-stone-600 mb-6 max-w-xl mx-auto">
              Start your product research with organized categories and helpful guides. Visit the main website to browse the full spreadsheet.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors text-sm md:text-base whitespace-nowrap"
              >
                CSSBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-stone-800 text-white font-medium rounded-lg hover:bg-stone-900 transition-colors text-sm md:text-base whitespace-nowrap"
              >
                {t('btn_buy_it_now')}
              </a>
            </div>
          </section>

          {/* Editorial Note */}
          <div className="p-4 md:p-5 rounded-lg bg-amber-50 border border-amber-100 mb-8">
            <p className="text-xs md:text-sm text-amber-700 leading-relaxed">
              <strong>Editorial Note:</strong> This article was last updated on August 27, 2026. Information about products, prices, links, and availability may have changed since then. We regularly review and update our content to ensure accuracy, but we recommend verifying all information directly before making any purchase. This website is an independent resource and does not officially represent CSSBuy.
            </p>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}